import React from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useT } from '../../i18n';

// The title and account row every signed-in portfolio page carries.
//
// It was copy-pasted across five pages, which is why the refresh control ended
// up at the BOTTOM of two of them: adding it to the row meant editing the same
// markup five times, and the footer was one place. Extracted so a control that
// belongs beside the account links can live there.
//
// `status` and `onRefresh` are optional. Notes and Transactions read data that
// is fetched fresh on every visit, so there is nothing for them to force.

/** Two arcs and two arrowheads, drawn rather than pulled from an icon set —
 *  this is the only icon on the page and a dependency for it would be larger
 *  than the file it lives in. `currentColor` so it inherits the row's hover
 *  and disabled colours instead of restating them. */
function RefreshIcon() {
    return (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
            <path d="M2.6 8a5.4 5.4 0 0 1 9.2-3.8" />
            <polyline points="11.8 1.8 11.8 4.2 9.4 4.2" />
            <path d="M13.4 8a5.4 5.4 0 0 1-9.2 3.8" />
            <polyline points="4.2 14.2 4.2 11.8 6.6 11.8" />
        </svg>
    );
}

export default function PortfolioHeader({ title, status, onRefresh, refreshing = false }) {
    const { user, signOut } = useAuth();
    const { t } = useT();

    /* A forced refresh re-fetches from the broker, which takes tens of seconds
       — IBKR generates the statement on demand and we poll for it — so the
       label says so rather than leaving the button looking hung. It is also
       disabled while it runs: the snapshot is one shared Postgres row, and a
       second concurrent rebuild would race the first to write it. */
    const label = refreshing ? t('holdings.refreshing') : t('holdings.refresh');

    return (
        <div className="holdings__header">
            <h2 className="heading styled">{title}</h2>
            {user && (
                <div className="holdings__user">
                    {status && <span className="holdings__status">{status}</span>}
                    <span>{user.email}</span>
                    <button type="button" className="holdings__signout" onClick={signOut}>
                        {t('common.signOut')}
                    </button>
                    {onRefresh && (
                        <button
                            type="button"
                            className={`holdings__refresh${refreshing ? ' is-refreshing' : ''}`}
                            onClick={onRefresh}
                            disabled={refreshing}
                            /* Icon-only, so the label has to be carried by the
                               accessible name rather than by visible text.
                               `title` gives the same words to a mouse; without
                               both, a circular arrow is the only thing anyone
                               gets to go on. */
                            aria-label={label}
                            title={label}
                        >
                            <RefreshIcon />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
